import { useCallback, useState } from 'react';

type ReadyState = {
  /**
   * if eddy has fired its ready event
   */
  isEddyReady: boolean;
  /**
   * if the preview has fired its ready event
   */
  isPreviewReady: boolean;
  /**
   * if the preview and eddy players have fired their ready events.  this is an
   * initialization gate where we first wait for isEddyReady and isPreviewReady
   * to become true, thereby setting isInitialized to true, but both individual
   * isReady flags can become false thereafter and not change the value of isInitialized.
   */
  isInitialized: boolean;
};

/**
 * manages the ready state of the preview and eddy players
 */
export default function useReadyState() {
  const [readyState, setReadyState] = useState<ReadyState>({
    isEddyReady: false,
    isPreviewReady: false,
    isInitialized: false,
  });

  const handleEddyReady = useCallback(() => {
    setReadyState(prev => ({
      ...prev,
      isEddyReady: true,
      isInitialized: prev.isInitialized || prev.isPreviewReady,
    }));
  }, []);

  const handlePreviewReady = useCallback(() => {
    setReadyState(prev => ({
      ...prev,
      isPreviewReady: true,
      isInitialized: prev.isInitialized || prev.isEddyReady,
    }));
  }, []);

  const handlePreviewDestroy = useCallback(() => {
    setReadyState(prev => ({
      ...prev,
      isPreviewReady: false,
    }));
  }, []);

  return {
    readyState,
    handleEddyReady,
    handlePreviewReady,
    handlePreviewDestroy,
  };
}
